import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import {
  createCoursePath,
  updateCoursePath,
} from '@/data/services/coursePathServices';
import CoursePath from '@/models/CoursePath';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';

import { updateCourse } from '@/data/services/courseServices';
import CourseForm, { CourseFormFields } from './CourseForm';

export type CourseFormServiceSuccess = {
  item: CoursePath;
  action: 'create' | 'update';
};

type CourseFormServiceProps = {
  onSuccess?: (success: CourseFormServiceSuccess) => void;
};

export default function CourseFormService({
  onSuccess,
}: CourseFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseForm.alerts',
  });
  const { coursePath, moduleId, cache } = usePedWorkspace();

  const handleCreate = async (
    data: CourseFormFields,
  ): Promise<CourseFormServiceSuccess> => {
    const created = await createCoursePath({
      lessonsQuantity: data.lessonsQuantity,
      course: {
        name: data.name,
        abbreviation: data.abbreviation,
        formats: data.formats,
        businessModels: data.businessModels,
        moduleId,
      },
    });
    alert.success(t('createSuccess'));
    return { item: created, action: 'create' };
  };

  const handleUpdate = async (
    data: CourseFormFields,
  ): Promise<CourseFormServiceSuccess> => {
    if (!coursePath) throw new Error('CoursePath not found');
    const payload = {
      name: data.name,
      abbreviation: data.abbreviation,
      formats: data.formats,
      businessModels: data.businessModels,
    };
    const courseChangedFiels = buildChangedObject(coursePath.course, payload);
    const coursePathChangedFields =
      data.lessonsQuantity !== (coursePath.lessonsQuantity ?? 0);

    if (
      Object.keys(courseChangedFiels).length === 0 &&
      !coursePathChangedFields
    ) {
      return { item: coursePath, action: 'update' };
    }

    const updatedCourse = Object.keys(courseChangedFiels).length
      ? await updateCourse(coursePath.course.id, courseChangedFiels)
      : coursePath.course;

    const updatedCoursePath = coursePathChangedFields
      ? await updateCoursePath(coursePath.slug, {
          lessonsQuantity: data.lessonsQuantity,
        })
      : coursePath;

    const coursePathUpdated = {
      ...coursePath,
      lessonsQuantity: updatedCoursePath.lessonsQuantity,
      course: updatedCourse,
      slug: updatedCourse.coursePathSlug ?? coursePath.slug,
    };
    alert.success(t('updateSuccess'));
    return { item: coursePathUpdated, action: 'update' };
  };

  const handleSubmit = async (
    data: CourseFormFields,
  ): Promise<CourseFormServiceSuccess> => {
    if (coursePath) return handleUpdate(data);
    return handleCreate(data);
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async response => {
      cache?.merge(response.item);
      onSuccess?.(response);
    },
  });

  return (
    <CourseForm
      course={coursePath?.course}
      lessonsQuantity={coursePath?.lessonsQuantity}
      onSubmit={onSubmit}
      isLoading={isPending}
    />
  );
}
